import React, { useState, useEffect } from 'react';
import { FaFolder, FaFileAlt, FaArrowLeft, FaCheck, FaSpinner } from 'react-icons/fa';

const API_BASE_URL = 'https://apissc.sainikschoolcadet.com/api';

const ImportQuestionModal = ({ isOpen, onClose, testId, onSuccess, currentColor }) => {
  // Navigation and data states
  const [folders, setFolders] = useState([]);
  const [tests, setTests] = useState([]);
  const [parentStack, setParentStack] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('folders'); // 'folders', 'questions'
  const [error, setError] = useState('');
  const [importSuccess, setImportSuccess] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      resetState();
      fetchRootFolders();
    }
  }, [isOpen]);
  
  const resetState = () => {
    setView('folders');
    setParentStack([]);
    setCurrentFolder(null);
    setSelectedTest(null);
    setSelectedQuestions([]);
    setTests([]);
    setQuestions([]);
    setError('');
    setImportSuccess(false);
  };
  
  // Fetch root folders
  const fetchRootFolders = async () => {
    setLoading(true);
    setError('');
    try {
      // Use the root/all endpoint to get root folders
      const response = await fetch(`${API_BASE_URL}/Folders/root/all`);
      if (!response.ok) throw new Error('Failed to fetch folders');
      
      const data = await response.json();
      setFolders(data.data || []);
      
      // Also fetch tests for the root level
      await fetchFolderContent(null);
      
      setCurrentFolder(null);
      setView('folders');
    } catch (err) {
      setError('Error loading folders: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch folder content (both subfolders and tests)
  const fetchFolderContent = async (folderId) => {
    setLoading(true);
    setError('');
    try {
      if (folderId !== null) {
        // Fetch subfolders
        const folderResponse = await fetch(`${API_BASE_URL}/Folders/parent/${folderId}`);
        if (!folderResponse.ok) throw new Error('Failed to fetch folders');
        
        const folderData = await folderResponse.json();
        setFolders(folderData.data || []);
      }
      
      // Fetch tests for this folder
      const testsUrl = folderId === null 
        ? `${API_BASE_URL}/Tests` 
        : `${API_BASE_URL}/Tests/folder/${folderId}`;
      
      const testsResponse = await fetch(testsUrl);
      if (!testsResponse.ok) throw new Error('Failed to fetch tests');
      
      const testsData = await testsResponse.json();
      setTests(testsData.data || []);
      
      setCurrentFolder(folderId);
      setView('folders');
    } catch (err) {
      setError('Error loading folder content: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Navigate to a specific folder
  const navigateToFolder = (folderId, folderName) => {
    // Add current folder to navigation stack
    if (currentFolder !== null) {
      setParentStack([...parentStack, { id: currentFolder, name: getCurrentFolderName() }]);
    }
    
    // Fetch content of the selected folder
    fetchFolderContent(folderId);
  };
  
  // Get current folder name from folders or parent stack
  const getCurrentFolderName = () => {
    if (currentFolder === null) return 'Root';
    
    const folder = folders.find(f => f.id === currentFolder);
    return folder ? folder.name : 'Folder';
  };
  
  // Navigate back to parent folder
  const navigateBack = () => {
    if (view === 'questions') {
      setView('folders');
      setQuestions([]);
      setSelectedQuestions([]);
      return;
    }
    
    if (parentStack.length === 0) {
      fetchRootFolders();
      return;
    }
    
    const newStack = [...parentStack];
    const prevFolder = newStack.pop();
    setParentStack(newStack);
    fetchFolderContent(prevFolder.id);
  };
  
  // Select a test and fetch its questions
  const selectTest = async (testId, testName) => {
    setLoading(true);
    setError('');
    setSelectedTest({ id: testId, name: testName });
    
    try {
      const response = await fetch(`${API_BASE_URL}/QuestionTestAssociations/test/${testId}/questions`);
      if (!response.ok) throw new Error('Failed to fetch questions');
      
      const data = await response.json();
      
      if (data.question_ids && data.question_ids.length > 0) {
        // Fetch details for all questions
        const questionPromises = data.question_ids.map(async (qId) => {
          const qResponse = await fetch(`${API_BASE_URL}/Questions/${qId}`);
          if (!qResponse.ok) throw new Error(`Failed to fetch question ${qId}`);
          return qResponse.json();
        });
        
        const questionDetails = await Promise.all(questionPromises);
        setQuestions(questionDetails);
      } else {
        setQuestions([]);
      }
      
      setView('questions');
    } catch (err) {
      setError('Error loading questions: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle question selection
  const toggleSelectAllQuestions = () => {
    if (selectedQuestions.length === questions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(questions.map(q => q.question_id));
    }
  };
  
  // Toggle single question selection
  const toggleQuestionSelection = (questionId) => {
    if (selectedQuestions.includes(questionId)) {
      setSelectedQuestions(selectedQuestions.filter(id => id !== questionId));
    } else {
      setSelectedQuestions([...selectedQuestions, questionId]);
    }
  };
  
  // Import selected questions
  const importQuestions = async () => {
    if (selectedQuestions.length === 0) {
      setError('Please select at least one question to import');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Create associations one by one for each question with the current test ID
      // This matches the model structure better than a bulk operation
      const importPromises = selectedQuestions.map(async (questionId) => {
        const response = await fetch(`${API_BASE_URL}/QuestionTestAssociations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            question_id: questionId,
            test_id: testId
          }),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `Failed to import question ${questionId}`);
        }
        
        return response.json();
      });
      
      await Promise.all(importPromises);
      
      setImportSuccess(true);
      
      // Notify parent component about successful import
      if (onSuccess) {
        onSuccess(selectedQuestions);
      }
      
      // Close modal after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError('Error importing questions: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Get option letter (A, B, C, D) based on option number
  const getOptionLetter = (optionNumber) => {
    return String.fromCharCode(65 + parseInt(optionNumber.split('_')[1]) - 1);
  };
  
  // Render the breadcrumb navigation
  const renderBreadcrumbs = () => {
    return (
      <div className="flex items-center text-sm text-gray-600 overflow-x-auto mb-4">
        <button 
          onClick={() => {
            resetState();
            fetchRootFolders();
          }}
          className="hover:text-blue-600 whitespace-nowrap"
        >
          Root
        </button>
        
        {parentStack.map((folder, index) => (
          <React.Fragment key={folder.id}>
            <span className="mx-1">/</span>
            <button 
              onClick={() => {
                // Navigate to this breadcrumb
                const newStack = parentStack.slice(0, index);
                setParentStack(newStack);
                fetchFolderContent(folder.id);
              }}
              className="hover:text-blue-600 whitespace-nowrap"
            >
              {folder.name}
            </button>
          </React.Fragment>
        ))}
        
        {currentFolder !== null && (
          <>
            <span className="mx-1">/</span>
            <span className="font-medium whitespace-nowrap">{getCurrentFolderName()}</span>
          </>
        )}
        
        {view === 'questions' && selectedTest && (
          <>
            <span className="mx-1">/</span>
            <span className="font-medium whitespace-nowrap">
              {selectedTest.name}
            </span>
          </>
        )}
      </div>
    );
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[85vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
            {view === 'folders' && 'Browse Folders and Tests'}
            {view === 'questions' && 'Select Questions to Import'}
          </h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Navigation and breadcrumbs */}
        <div className="mb-4 flex items-center">
          {(view !== 'folders' || parentStack.length > 0) && (
            <button 
              onClick={navigateBack}
              className="mr-3 p-1 rounded hover:bg-gray-100"
            >
              <FaArrowLeft />
            </button>
          )}
          {renderBreadcrumbs()}
        </div>
        
        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-800 rounded-md">
            {error}
          </div>
        )}
        
        {/* Success message */}
        {importSuccess && (
          <div className="mb-4 p-3 bg-green-100 text-green-800 rounded-md flex items-center">
            <FaCheck className="mr-2" /> Questions imported successfully!
          </div>
        )}
        
        {/* Main content area */}
        <div className="flex-1 overflow-y-auto mb-4">
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <FaSpinner className="animate-spin h-10 w-10 text-blue-600" />
            </div>
          ) : (
            <>
              {/* Folders and Tests View */}
              {view === 'folders' && (
                <div className="space-y-4">
                  {/* Folders section */}
                  {folders.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2 text-gray-700">Folders</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {folders.map(folder => (
                          <div 
                            key={folder.id}
                            className="border rounded-md p-3 flex items-center cursor-pointer hover:bg-gray-50"
                            onClick={() => navigateToFolder(folder.id, folder.name)}
                          >
                            <FaFolder className="text-yellow-400 mr-3" size={20} />
                            <div className="flex-1 truncate">{folder.name}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Tests section */}
                  <div className="mt-6">
                    <h4 className="font-medium mb-2 text-gray-700">Tests</h4>
                    <div className="grid grid-cols-1 gap-3">
                      {tests.length === 0 ? (
                        <div className="text-center py-6 text-gray-500 border rounded-md">
                          No tests found in this folder
                        </div>
                      ) : (
                        tests.map(test => (
                          <div 
                            key={test.test_id}
                            className="border rounded-md p-4 cursor-pointer hover:bg-gray-50"
                            onClick={() => selectTest(test.test_id, test.name)}
                          >
                            <div className="flex items-center">
                              <FaFileAlt className="text-blue-500 mr-3" size={18} />
                              <div className="flex-1">
                                <div className="font-medium">{test.name}</div>
                                <div className="text-sm text-gray-500">
                                  Duration: {test.time_duration} mins
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Questions View */}
              {view === 'questions' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-2 pb-2 border-b">
                    <div className="font-medium">
                      Available Questions: {questions.length}
                    </div>
                    <button
                      onClick={toggleSelectAllQuestions}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {selectedQuestions.length === questions.length 
                        ? "Deselect All" 
                        : "Select All"}
                    </button>
                  </div>
                  
                  <div className="border rounded-md divide-y overflow-hidden">
                    {questions.length === 0 ? (
                      <div className="text-center py-10 text-gray-500">
                        No questions found in this test
                      </div>
                    ) : (
                      questions.map((question, index) => (
                        <div 
                          key={question.question_id} 
                          className={`p-4 hover:bg-gray-50 ${
                            selectedQuestions.includes(question.question_id) ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="flex items-start mb-3">
                            <input
                              type="checkbox"
                              id={`question-${question.question_id}`}
                              className="h-5 w-5 text-blue-600 rounded mt-1 mr-3"
                              checked={selectedQuestions.includes(question.question_id)}
                              onChange={() => toggleQuestionSelection(question.question_id)}
                            />
                            <div className="flex-1">
                              <div className="font-medium mb-2">
                                Question {index + 1} 
                                <span className="text-sm text-gray-500 ml-2">
                                  ({question.marks || 1} {question.marks === 1 ? 'mark' : 'marks'})
                                </span>
                              </div>
                              
                              {/* Question Text */}
                              <div 
                                className="mb-3"
                                dangerouslySetInnerHTML={{ __html: question.question_text }}
                              />
                              
                              {/* Question Options */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-4">
                                {['option_1', 'option_2', 'option_3', 'option_4'].map((option) => (
                                  question[option] && (
                                    <div 
                                      key={option}
                                      className={`p-2 rounded ${
                                        question.correct_option === option 
                                          ? 'bg-green-50 text-green-800 border border-green-200' 
                                          : ''
                                      }`}
                                    >
                                      <span className="font-medium mr-2">{getOptionLetter(option)}.</span>
                                      <span dangerouslySetInnerHTML={{ __html: question[option] }} />
                                    </div>
                                  )
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex justify-between pt-3 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          
          {view === 'questions' && (
            <button
              onClick={importQuestions}
              className="px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white"
              style={{ backgroundColor: currentColor }}
              disabled={selectedQuestions.length === 0 || loading}
            >
              {loading ? 'Importing...' : `Import ${selectedQuestions.length} Questions`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportQuestionModal;